import * as z from 'zod';

const PathsSchema = z.object({
  auth: z.object({
    signIn: z.string().min(1),
    signUp: z.string().min(1),
    verifyMfa: z.string().min(1),
    callback: z.string().min(1),
    passwordReset: z.string().min(1),
    passwordUpdate: z.string().min(1),
  }),
  app: z.object({
    home: z.string().min(1),
    personalAccountSettings: z.string().min(1),
    personalAccountBilling: z.string().min(1),
    personalAccountBillingReturn: z.string().min(1),
    accountHome: z.string().min(1),
    accountVoice: z.string().min(1),
    accountVoiceBuilder: z.string().min(1),
    accountRouting: z.string().min(1),
    accountActivity: z.string().min(1),
    accountInbox: z.string().min(1),
    accountSupportTeams: z.string().min(1),
    accountOutreach: z.string().min(1),
    accountCalls: z.string().min(1),
    accountReceptionist: z.string().min(1),
    accountAgents: z.string().min(1),
    accountAgentTemplates: z.string().min(1),
    accountAgentBuilder: z.string().min(1),
    accountConfiguration: z.string().min(1),
    accountBroadcasts: z.string().min(1),
    accountBroadcastBuilder: z.string().min(1),
    accountAnalytics: z.string().min(1),
    accountFleet: z.string().min(1),
    accountIntegrations: z.string().min(1),
    accountKnowledge: z.string().min(1),
    accountLanguage: z.string().min(1),
    accountNotifications: z.string().min(1),
    accountCampaigns: z.string().min(1),
    accountAeo: z.string().min(1),
    accountExchange: z.string().min(1),
    accountInsurance: z.string().min(1),
    accountItsm: z.string().min(1),
    accountOps: z.string().min(1),
    accountAudit: z.string().min(1),
    accountSettings: z.string().min(1),
    accountBilling: z.string().min(1),
    accountPlan: z.string().min(1),
    accountMembers: z.string().min(1),
    accountBillingReturn: z.string().min(1),
    accountProfileSettings: z.string().min(1),
    accountApiKeys: z.string().min(1),
    createTeam: z.string().min(1),
    adminHome: z.string().min(1),
    joinTeam: z.string().min(1),
  }),
});

const pathsConfig = PathsSchema.parse({
  auth: {
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    verifyMfa: '/auth/verify',
    callback: '/auth/callback',
    passwordReset: '/auth/password-reset',
    passwordUpdate: '/update-password',
  },
  app: {
    home: '/home',
    personalAccountSettings: '/home/settings',
    personalAccountBilling: '/home/billing',
    personalAccountBillingReturn: '/home/billing/return',
    accountHome: '/home/[account]',
    // Operator console, ported from concierge-chat's flat `/aeo`, `/ops` and
    // `/audit` routes — team-account scoped here, so the tenant comes from the
    // account rather than a `?client=` query parameter.

    accountActivity: `/home/[account]/activity`,
    /*
     * The e& platform's unified Inbox (EAND-SPEC §4.1, §5.7) — every channel,
     * one list, with a human takeover.
     *
     * NOT `accountActivity`, which is the AI receptionist's exception queue and
     * has carried the sidebar label "Inbox" since before this product existed.
     * They are two screens for two products (`inbound` gates one, `inbox` the
     * other) and a tenant runs one or the other; sharing a path would have made
     * that accidental rather than deliberate.
     */
    accountInbox: `/home/[account]/inbox`,
    accountSupportTeams: `/home/[account]/teams`,
    accountOutreach: `/home/[account]/outreach`,
    accountCalls: `/home/[account]/calls`,
    accountReceptionist: `/home/[account]/receptionist`,
    // The Conversational AI Platform's Agent Builder (EAND-SPEC §4.1).
    accountAgents: `/home/[account]/agents`,
    /*
     * A STATIC segment beside `agents/[id]`, and Next resolves static before
     * dynamic — so this is the builder tab and not an agent whose id is the
     * word "builder". The `[id]` route parses a uuid and 404s on anything
     * else, so the two cannot be confused even if that ordering changed.
     */
    accountAgentTemplates: `/home/[account]/agents/templates`,
    accountAgentBuilder: `/home/[account]/agents/builder`,
    /*
     * Configuration export / import (RFQ §9.1.4) — the Agent Builder's
     * definitions as YAML documents a tenant commits, and reads back as drafts.
     *
     * Gated on the same `agents` operation as the Builder, because these ARE
     * the Builder's documents: a tenant without that screen has nothing to
     * export and nowhere to review an import.
     */
    accountConfiguration: `/home/[account]/configuration`,
    // The end users' personal Concierge agents. Not to be confused with
    // `accountVoice`, which is the tenant's own agent — see the note in
    // team-account-navigation.config.tsx.
    accountFleet: `/home/[account]/fleet`,
    accountIntegrations: `/home/[account]/integrations`,
    accountKnowledge: `/home/[account]/knowledge`,
    accountLanguage: `/home/[account]/language`,
    accountNotifications: `/home/[account]/notifications`,
    accountCampaigns: `/home/[account]/campaigns`,
    /*
     * S16's campaign screens (EAND-SPEC §5.13, RFQ 2.2.48–2.2.51): audience
     * preview, the template approval workflow, and the two stops.
     *
     * **NOT `accountCampaigns`.** That path is the Concierge / Speed-to-Lead
     * campaigns tab, gated on `outreach`, running the Concierge engine over
     * `customer_records`. This one runs `@kit/campaigns` over gateway contacts
     * and TBP transport, and is gated on `broadcasts`. Two products, two
     * engines, two permissions — and a tenant can run both at once, so one
     * word between the two sidebar entries would repeat the "Inbox / Inbox"
     * mistake the `conversations` label exists to fix.
     *
     * "Broadcasts" is the RFQ's own word for what this does — 2.2.48 is
     * "segment-based broadcast" — and names the act rather than the artefact.
     */
    accountBroadcasts: `/home/[account]/broadcasts`,
    accountBroadcastBuilder: `/home/[account]/broadcasts/builder`,
    /*
     * The Analytics & Insights dashboard (EAND-SPEC §5, RFQ 2.2.52-2.2.58):
     * volume, governance outcomes, agent performance by published version,
     * channel mix, containment and CSAT, per tenant and per date range.
     *
     * **NOT `accountAudit`.** That path is the Concierge audit ledger — a list
     * of individual governed events for a compliance reader. This is the
     * aggregate view over `agent_turns` and `conversation_ratings`, and the two
     * answer different questions: "what did the agent do at 14:02" versus "how
     * often does the agent hand over".
     */
    accountAnalytics: `/home/[account]/analytics`,
    accountAeo: `/home/[account]/aeo`,
    accountExchange: `/home/[account]/exchange`,
    accountInsurance: `/home/[account]/insurance`,
    accountItsm: `/home/[account]/itsm`,
    accountOps: `/home/[account]/ops`,
    accountAudit: `/home/[account]/audit`,
    accountSettings: `/home/[account]/settings`,
    accountBilling: `/home/[account]/billing`,
    /*
     * Plan and usage, which is NOT billing.
     *
     * Billing is postpaid through e&, so there is no checkout here — this is
     * the read-only account page: what the tenant is licensed for and what
     * they have consumed this period.
     */
    accountPlan: `/home/[account]/plan`,
    accountMembers: `/home/[account]/members`,
    accountBillingReturn: `/home/[account]/billing/return`,
    accountProfileSettings: `/home/[account]/settings/profile`,
    /*
     * The tenant's own credentials for the public API (RFQ 4.10).
     *
     * Inside `settings/` deliberately. These are the tenant's keys to the
     * tenant's own data at a role the tenant already grants, so the surface is
     * account settings and the gate is `settings.manage` — owner and admin.
     *
     * **NOT `accountIntegrations`.** That screen holds connector credentials,
     * which read a THIRD PARTY's system — a bank's whole helpdesk — and are
     * admin-only for that reason. Same tier today, different blast radius, and
     * sharing a screen would make the two look like one kind of secret.
     */
    accountApiKeys: `/home/[account]/settings/api-keys`,
    /*
     * The tenant's own voice agent — persona, speaker, test call.
     *
     * Moved out of `/settings/voice`. It is a top-level entry in the sidebar
     * now, and living inside the settings segment meant inheriting that
     * layout's page header and breadcrumbs on top of its own — the screen
     * rendered "Home › Padra › Settings › Voice" twice. Nothing outside
     * paths.config referenced the old path.
     */
    accountRouting: `/home/[account]/routing`,
    accountVoice: `/home/[account]/voice`,
    accountVoiceBuilder: `/home/[account]/voice/builder`,
    createTeam: '/home/create-team',
    /*
     * The operator console. A super admin has no tenant by design, so with
     * teams-only on this is where "home" means for e& staff — see
     * `workspace-destination.ts`.
     */
    adminHome: '/admin',
    joinTeam: '/join',
  },
} satisfies z.output<typeof PathsSchema>);

export default pathsConfig;
